import { useCallback, useEffect, useState } from 'react';
import {
  defaultFilterValues,
  FILTERS,
  type FilterControl,
  type FilterId,
} from '../../filters';
import type { DocumentMeta } from '../../core/types';
import { getEngine } from '../../engine/engine';
import { renderFilterPreview } from '../../engine/ops';
import { applyFilterValues } from '../../state/actions';
import { liveRender } from '../../state/renderState';
import { Checkbox, Modal, Select, SliderRow } from '../components/controls';

/**
 * Live preview mirrors the Adjustments dialog: the filtered result stands in for
 * the layer until the user confirms, so Cancel leaves the pixels untouched.
 */
export function FilterDialog({
  id,
  doc,
  open,
  onClose,
}: {
  id: FilterId;
  doc: DocumentMeta;
  open: boolean;
  onClose: () => void;
}) {
  const definition = FILTERS[id];
  const [values, setValues] = useState(() => defaultFilterValues(id));
  const [preview, setPreview] = useState(true);
  const engine = getEngine();

  const clearPreview = useCallback(() => {
    if (doc.activeLayerId) liveRender.overrides.delete(doc.activeLayerId);
    engine.invalidate();
  }, [doc.activeLayerId, engine]);

  useEffect(() => {
    const layerId = doc.activeLayerId;
    if (!open || !layerId || !engine.available) return;
    if (!preview) {
      clearPreview();
      return;
    }
    const texture = renderFilterPreview(engine, doc, layerId, id, values);
    liveRender.overrides.set(layerId, texture);
    engine.invalidate();
  }, [open, preview, values, doc, engine, clearPreview, id]);

  useEffect(() => clearPreview, [clearPreview]);

  const close = () => {
    clearPreview();
    onClose();
  };

  const apply = () => {
    clearPreview();
    applyFilterValues(id, values);
    onClose();
  };

  const setValue = (key: string, value: number) =>
    setValues((current) => ({ ...current, [key]: value }));

  return (
    <Modal
      open={open}
      title={definition.label}
      onClose={close}
      footer={
        <>
          <button
            type="button"
            className="button"
            onClick={() => setValues(defaultFilterValues(id))}
          >
            Reset
          </button>
          <button type="button" className="button" onClick={close}>
            Cancel
          </button>
          <button type="button" className="button button--primary" onClick={apply}>
            OK
          </button>
        </>
      }
    >
      {definition.controls.length === 0 ? (
        <p className="dialog__note">This filter has no options.</p>
      ) : (
        definition.controls.map((control) => (
          <FilterControlRow
            key={control.key}
            control={control}
            value={values[control.key]}
            onChange={(value) => setValue(control.key, value)}
          />
        ))
      )}
      <Checkbox label="Preview" checked={preview} onChange={setPreview} />
    </Modal>
  );
}

function FilterControlRow({
  control,
  value,
  onChange,
}: {
  control: FilterControl;
  value: number;
  onChange: (value: number) => void;
}) {
  if (control.kind === 'slider') {
    return (
      <SliderRow
        label={control.label}
        value={value}
        min={control.min}
        max={control.max}
        step={control.step}
        onChange={onChange}
      />
    );
  }
  if (control.kind === 'toggle') {
    return (
      <Checkbox
        label={control.label}
        checked={value > 0.5}
        onChange={(checked) => onChange(checked ? 1 : 0)}
      />
    );
  }
  return (
    <div className="field">
      <span className="field__label">{control.label}</span>
      <Select
        value={String(value)}
        options={control.options.map((option) => ({
          value: String(option.value),
          label: option.label,
        }))}
        onChange={(next) => onChange(Number(next))}
      />
    </div>
  );
}
